import React, { useState, useCallback, useEffect } from 'react'
import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { LuDollarSign } from 'react-icons/lu'
import Moment from 'moment-timezone'

import { Loader, YSpacing } from '@components/common'

import { moneyString } from '@utils'
import {
  GroupOrder,
  ClientGlobalSettings,
  CustomerCancelledSubOrdersResp,
  CancelledObjectType,
  SubOrderCancellationAction,
  GetCancelledSubOrdersReq,
  CancellationSubOrder,
  CancellationSecondarySubOrder,
  SubsidyType,
} from '@types'

interface CancelledGroupOrderTableProps {
  groupOrder: GroupOrder
  clientGlobalSettings: ClientGlobalSettings | null
  subOrderIds: string[]
  cancelledId?: string
  cancelledType?: CancelledObjectType
  totalAffectedOrders: number
  getCancelledSubOrders: (
    grpId: string,
    req: GetCancelledSubOrdersReq,
  ) => Promise<CustomerCancelledSubOrdersResp[] | null>
}

const CancelledGroupOrderTable: React.FC<CancelledGroupOrderTableProps> = ({
  groupOrder,
  clientGlobalSettings,
  subOrderIds,
  cancelledId,
  cancelledType,
  totalAffectedOrders,
  getCancelledSubOrders,
}) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)
  const [cancelledSubOrders, setCancelledSubOrders] = useState<
    CustomerCancelledSubOrdersResp[]
  >([])

  const loadCancelledSubOrders = useCallback(async () => {
    if (!groupOrder || !cancelledId || !cancelledType) {
      return
    }
    setLoading(true)
    const subOrders = await getCancelledSubOrders(groupOrder.id, {
      subOrderIds,
      cancelledObjectId: cancelledId,
      cancelledObjectType: cancelledType,
    })
    if (subOrders) {
      setCancelledSubOrders(subOrders)
    }
    setLoading(false)
  }, [
    groupOrder,
    cancelledId,
    cancelledType,
    subOrderIds,
    getCancelledSubOrders,
  ])

  useEffect(() => {
    loadCancelledSubOrders()
  }, [loadCancelledSubOrders])

  const toggleRow = (orderId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }))
  }

  return (
    <div className="w-full flex flex-col mb-2">
      <div className="text-lg font-bold mb-2">
        Impacted Orders ({totalAffectedOrders})
      </div>
      {loading ? (
        <Loader width="20%" isCenter />
      ) : (
        <div className="flex flex-col w-full p-4">
          {cancelledSubOrders.map((customerCancelledSubOrders) => (
            <CustomerCancelledSubOrders
              key={customerCancelledSubOrders.id}
              groupOrder={groupOrder}
              clientGlobalSettings={clientGlobalSettings}
              customerCancelledSubOrders={customerCancelledSubOrders}
              expanded={expandedRows[customerCancelledSubOrders.id] || false}
              onToggle={() => toggleRow(customerCancelledSubOrders.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default CancelledGroupOrderTable

interface CustomerCancelledSubOrdersProps {
  groupOrder: GroupOrder | null
  clientGlobalSettings: ClientGlobalSettings | null
  customerCancelledSubOrders: CustomerCancelledSubOrdersResp
  expanded: boolean
  onToggle: () => void
}
const CustomerCancelledSubOrders: React.FC<CustomerCancelledSubOrdersProps> = ({
  groupOrder,
  clientGlobalSettings,
  customerCancelledSubOrders,
  expanded,
  onToggle,
}) => {
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>(
    {},
  )
  const subsidyType = groupOrder?.subsidySettings?.subsidyType
  const subsidyTypeStr =
    subsidyType === SubsidyType.HUNGRY_WALLET ? 'Wallet' : 'Subsidy'

  const {
    updatedOrders,
    sameGroupOrderUpdatedOrders,
    externalWalletUpdatedOrders,
  } = customerCancelledSubOrders
  const totalCustomerPaid = updatedOrders.reduce(
    (acc, { orderDetails }) =>
      acc + parseFloat(orderDetails.originalEmployeeAmount),
    0,
  )
  const totalEmployerPaid = updatedOrders.reduce(
    (acc, { orderDetails }) =>
      acc + parseFloat(orderDetails.originalSubsidyAmount),
    0,
  )
  const additionalOrders = [
    ...sameGroupOrderUpdatedOrders,
    ...externalWalletUpdatedOrders,
  ]

  const affectedOrdersCount =
    updatedOrders.length +
    sameGroupOrderUpdatedOrders.length +
    externalWalletUpdatedOrders.length

  const onToggleOrder = (orderId: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }))
  }

  const renderOrder = (order: CancellationSubOrder) => {
    const {
      action,
      orderDetails: {
        originalEmployeeAmount,
        originalSubsidyAmount,
        newEmployeeAmount,
        newSubsidyAmount,
        refundOwedToCustomer,
        remainingItems,
        removedItems,
      },
    } = order

    const background =
      action === SubOrderCancellationAction.CANCEL
        ? 'bg-red-200'
        : 'bg-orange-200'
    const actionBackground =
      action === SubOrderCancellationAction.CANCEL
        ? 'bg-red-500'
        : 'bg-orange-500'
    const actionStr =
      action === SubOrderCancellationAction.CANCEL ? 'Cancelled' : 'Modified'

    return (
      <div className="rounded-md mb-2" style={{ border: '1px solid #cbd5e0' }}>
        <div
          className={`flex flex-row justify-between items-center p-2 cursor-pointer ${background}`}
          onClick={() => onToggleOrder(order.id)}
        >
          <div className="flex flex-row items-center">
            {expandedOrders[order.id] ? <FaChevronDown /> : <FaChevronRight />}
            <div className="flex flex-col ml-2">
              <p className="text-md font-bold">
                {order.orderNumber}
                <span
                  className={`${actionBackground} px-2 py-1 rounded-md ml-2 text-white text-sm`}
                >
                  {actionStr}
                </span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Order Date: {Moment(order.orderDate).format('MMM D, YYYY')}
              </p>
            </div>
          </div>
          <div className="flex flex-row items-center">
            <div className="flex flex-col">
              <p className="text-center">Employee Paid</p>
              <p className="text-center">
                <span className="text-gray-500 line-through">
                  ${moneyString(originalEmployeeAmount)}
                </span>{' '}
                →{' '}
                <span className="text-green-500">
                  ${moneyString(newEmployeeAmount)}
                </span>
              </p>
            </div>
            <div
              className="flex flex-col px-2 mx-2"
              style={{
                borderRight: '1px solid #cbd5e0',
                borderLeft: '1px solid #cbd5e0',
              }}
            >
              <p className="text-center">{subsidyTypeStr} Amount</p>
              <p className="text-center">
                <span className="text-gray-500 line-through">
                  ${moneyString(originalSubsidyAmount)}
                </span>{' '}
                →{' '}
                <span className="text-blue-500">
                  ${moneyString(newSubsidyAmount)}
                </span>
              </p>
            </div>
            <div className="flex flex-col">
              <p className="text-center">Employee Refund</p>
              <p className="text-center">
                <span className="text-red-500">
                  ${moneyString(refundOwedToCustomer)}
                </span>
              </p>
            </div>
          </div>
        </div>
        {expandedOrders[order.id] && (
          <div className="flex flex-col w-full px-8 py-4 bg-gray-100 gap-2">
            <p className="font-bold text-md">Items in this suborder:</p>
            {removedItems.map((item) => (
              <div
                key={item.id}
                className="flex flex-row items-center justify-between bg-red-200 p-2 mb-2 rounded-md"
                style={{ border: '1px solid #f56565' }}
              >
                <p className="text-red-500 font-bold">
                  {item.itemName}
                  <span className="ml-2 px-2 py-1 rounded-md bg-red-500 text-white text-sm font-normal">
                    DELETED
                  </span>
                </p>
                <p className="text-gray-500">Qty: {item.quantity}</p>
              </div>
            ))}
            {remainingItems.map((item) => (
              <div
                key={item.id}
                className="flex flex-row items-center justify-between p-2 mb-2 rounded-md bg-white"
                style={{ border: '1px solid #cbd5e0' }}
              >
                <p className="text-gray-700 font-bold">{item.itemName}</p>
                <p className="text-gray-500">Qty: {item.quantity}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderAdditionalOrders = (orders: CancellationSecondarySubOrder[]) => {
    return (
      <>
        <YSpacing height="5px" />
        <div
          className="flex flex-col bg-blue-100 rounded-md p-4"
          style={{ border: '1px solid #4299e1' }}
        >
          <div className="flex flex-row items-center">
            <LuDollarSign className="text-blue-500 mr-2" />
            <span className=" text-white bg-blue-500 px-2 py-1 rounded-md mr-2">
              Indirectly Affected
            </span>
            <p className="text-blue-700 font-bold">
              {subsidyTypeStr} Reallocation
            </p>
          </div>
          {subsidyType === SubsidyType.HUNGRY_WALLET ? (
            <p className="my-2">
              The refunded wallet amount can be reallocated to other orders
              where {customerCancelledSubOrders.customerName} paid out of pocket
              for the same Group Order or different Group Orders within the same
              wallet period (
              {clientGlobalSettings?.hungryWalletSettings?.durationType}).
              Credits are applied to the most expensive order first.
            </p>
          ) : (
            <p className="my-2">
              The refunded subsidy amount can be reallocated to other orders
              where {customerCancelledSubOrders.customerName} paid out of pocket
              for this Group Order.
            </p>
          )}
          {orders.map((order) => (
            <div
              key={order.id}
              className="flex flex-row items-center justify-between mb-2 rounded-md p-2 bg-white"
              style={{ border: '1px solid #4299e1' }}
            >
              <div className="flex flex-col">
                <p className="text-blue-700 font-bold">{order.orderNumber}</p>
                <p className="text-gray-500">
                  Order Date: {Moment(order.orderDate).format('MMM D, YYYY')}
                </p>
              </div>
              <div className="flex flex-row items-center">
                <div className="flex flex-col">
                  <p className="text-gray-500 text-center">Employee Amount</p>
                  <p className="text-gray-500 text-center">
                    ${moneyString(order.employeeAmount)} →{' '}
                    <span className="text-green-500">
                      ${moneyString(order.newEmployeeAmount)}
                    </span>
                  </p>
                </div>
                <div
                  className="flex flex-col px-2 mx-2"
                  style={{
                    borderRight: '1px solid #cbd5e0',
                    borderLeft: '1px solid #cbd5e0',
                  }}
                >
                  <p className="text-gray-500 text-center">
                    {subsidyTypeStr} Amount
                  </p>
                  <p className="text-gray-500 text-center">
                    ${moneyString(order.subsidyAmount)} →{' '}
                    <span className="text-blue-500">
                      ${moneyString(order.newSubsidyAmount)}
                    </span>
                  </p>
                </div>
                <div className="flex flex-col">
                  <p className="text-gray-500 text-center">Employee Refund</p>
                  <p className="text-gray-500 text-center">
                    ${moneyString(order.employeeRefundAmount)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    )
  }

  return (
    <div
      style={{ border: '1px solid #cbd5e0' }}
      className="bg-gray-200 rounded-md"
    >
      <div
        className="flex flex-row justify-between items-center p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex flex-row items-center">
          {expanded ? <FaChevronDown /> : <FaChevronRight />}
          <div className="flex flex-col ml-2">
            <p className="text-lg font-bold">
              {customerCancelledSubOrders.customerName}
            </p>
            <p className="text-sm text-gray-500">
              {customerCancelledSubOrders.customerEmail}
            </p>
          </div>
        </div>
        <div className="flex flex-row items-center">
          <div className="flex flex-col justify-center">
            <p className="text-lg font-bold text-center">
              {affectedOrdersCount}
            </p>
            <p className="text-sm text-gray-500 text-center">Orders</p>
          </div>
          <div className="flex flex-col text-right ml-4">
            <p>
              {subsidyTypeStr}:{' '}
              <span className="text-blue-500 font-bold">
                ${totalEmployerPaid.toFixed(2)}
              </span>
            </p>
            <p>
              Employee Paid:{' '}
              <span className="text-green-500 font-bold">
                ${totalCustomerPaid.toFixed(2)}
              </span>
            </p>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="flex flex-col w-full px-8 py-4 bg-white">
          {customerCancelledSubOrders.updatedOrders.length > 0 && (
            <>
              <div className="flex flex-row items-center mb-2">
                <span className=" text-white bg-red-500 px-2 py-1 rounded-md mr-2">
                  Directly Affected
                </span>
                <span className="font-bold">Suborders Being Modified</span>
              </div>
              {customerCancelledSubOrders.updatedOrders.map(renderOrder)}
            </>
          )}
          {additionalOrders.length > 0 &&
            renderAdditionalOrders(additionalOrders)}
        </div>
      )}
    </div>
  )
}
